import React from "react";
import {
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Input,
  Link,
  Spinner,
  Switch,
  Text,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import "@fontsource/roboto";

import * as lib from "../lib/lib";

function toDatetimeString(unix, utc) {
  const d = new Date(unix * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  if (utc) {
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fromDatetimeString(str, utc) {
  if (utc) {
    return Math.floor(new Date(str + "Z").getTime() / 1000);
  }
  return Math.floor(new Date(str).getTime() / 1000);
}

function startOfMonthUTC() {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) / 1000;
}

const Timestamp = () => {
  const initialTs = startOfMonthUTC();
  const [useUTC, setUseUTC] = React.useState(true);
  const [unixTimestamp, setUnixTimestamp] = React.useState(initialTs);
  const [datetimeValue, setDatetimeValue] = React.useState(
    toDatetimeString(initialTs, true),
  );
  const [blockHeight, setBlockHeight] = React.useState(null);
  const [blockTimestamp, setBlockTimestamp] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const debounceRef = React.useRef(null);

  const fetchBlock = React.useCallback(async (ts) => {
    setLoading(true);
    setError(null);
    setBlockHeight(null);
    setBlockTimestamp(null);
    try {
      const res = await lib.getMidgard(`/v2/debug/block/${ts}`);
      const nextHeight = parseInt(res.data.blockHeight) + 1;
      setBlockHeight(nextHeight);
      const nextRes = await lib.getThornode(`thorchain/block`, {
        height: nextHeight,
      });
      const nextTs = nextRes.data?.header?.time;
      if (nextTs) {
        setBlockTimestamp(Math.floor(new Date(nextTs).getTime() / 1000));
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to fetch block");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchBlock(initialTs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUTCToggle = () => {
    const next = !useUTC;
    setUseUTC(next);
    setDatetimeValue(toDatetimeString(unixTimestamp, next));
  };

  const debouncedFetchBlock = React.useCallback(
    (ts) => {
      clearTimeout(debounceRef.current);
      setLoading(true);
      debounceRef.current = setTimeout(() => fetchBlock(ts), 3000);
    },
    [fetchBlock],
  );

  const handleUnixChange = (e) => {
    const val = e.target.value;
    setUnixTimestamp(val);
    const num = parseInt(val, 10);
    if (!isNaN(num) && String(num).length >= 10) {
      setDatetimeValue(toDatetimeString(num, useUTC));
      debouncedFetchBlock(num);
    }
  };

  const handleDatetimeChange = (e) => {
    const val = e.target.value;
    setDatetimeValue(val);
    const ts = fromDatetimeString(val, useUTC);
    if (!isNaN(ts)) {
      setUnixTimestamp(ts);
      debouncedFetchBlock(ts);
    }
  };

  return (
    <Card maxW="480px" w="100%">
      <CardBody>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Timestamp → Block Height</Heading>
          <Flex align="center" gap={2}>
            <Text fontSize="xs" color="gray.500">
              Local
            </Text>
            <Switch size="sm" isChecked={useUTC} onChange={handleUTCToggle} />
            <Text fontSize="xs" fontWeight={useUTC ? "bold" : "normal"}>
              UTC
            </Text>
          </Flex>
        </Flex>

        <Flex direction="column" gap={3}>
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.600">
              Date & Time ({useUTC ? "UTC" : "local"})
            </Text>
            <Input
              type="datetime-local"
              step="1"
              value={datetimeValue}
              onChange={handleDatetimeChange}
              fontFamily="mono"
            />
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.600">
              Unix Timestamp (seconds)
            </Text>
            <Input
              type="number"
              value={unixTimestamp}
              onChange={handleUnixChange}
              fontFamily="mono"
            />
          </Box>

          <Box
            mt={2}
            p={3}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
              THORChain Block
            </Text>
            {loading ? (
              <Spinner size="sm" />
            ) : error ? (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            ) : blockHeight ? (
              <Flex direction="column" gap={1}>
                <Flex align="center" gap={2}>
                  <Text fontSize="2xl" fontWeight="bold" fontFamily="mono">
                    {blockHeight}
                  </Text>
                  <Link
                    href={lib.blockExplorerLink("THOR", blockHeight)}
                    isExternal
                    color="blue.500"
                  >
                    <ExternalLinkIcon />
                  </Link>
                </Flex>
                {blockTimestamp && (
                  <Text fontSize="xs" color="gray.500">
                    Block time:{" "}
                    {useUTC
                      ? new Date(blockTimestamp * 1000).toUTCString()
                      : new Date(blockTimestamp * 1000).toLocaleString()}
                  </Text>
                )}
              </Flex>
            ) : null}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default Timestamp;
